import React, { useState, useEffect } from 'react';
import { C, FONT } from './driver-tokens.js';
import { getToken, clearToken, api, apiPost } from './auth/driver-session.js';
import { LoginScreen, SetupScreen, ForgotScreen, ResetPasswordScreen } from './auth/AuthScreens.js';
import VerifyWizard from './auth/VerifyWizard.js';
import InviteFlow from './auth/InviteFlow.js';
import HomeScreen from './home/HomeScreen.js';
import BottomNav from './home/BottomNav.js';
import ClockInFlow from './clock/ClockInFlow.js';
import ClockOutFlow from './clock/ClockOutFlow.js';
import StopsScreen from './stops/StopsScreen.js';
import ScheduleScreen from './schedule/ScheduleScreen.js';
import ProfileScreen from './profile/ProfileScreen.js';
import DriverOnboardingWizard from './profile/DriverOnboardingWizard.js';

export interface DriverEmployee {
  id?: string | number;
  driver_id?: string;
  first_name?: string;
  last_name?: string;
  nickname?: string;
  phone?: string;
  photo_url?: string;
  profile_completed?: boolean;
  shirt_size?: string;
  pants_size?: string;
  jacket_size?: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  emergency_contact_relationship?: string;
  address_line1?: string;
  address_line2?: string;
  address_city?: string;
  address_state?: string;
  address_zip?: string;
  dob?: string;
  dl_number?: string;
}

interface HomeResponse {
  employee?: DriverEmployee;
  feature_flags?: Record<string, { enabled?: boolean }>;
  clock_status?: { clocked_in?: boolean; clock_in?: { clock_in_timestamp?: string } };
  daily_log?: { stops?: number; miles?: number; threshold_used?: string | number; stop_bonus?: string | number };
  weekly_totals?: { stops?: number; miles?: number; stop_bonus?: string | number };
  current_assignment?: { terminals?: { name?: string }; assignment_type?: string; started_at?: string };
  error?: string;
}

type ViewState = 'LOADING' | 'AUTH_LOGIN' | 'AUTH_SETUP' | 'AUTH_FORGOT' | 'AUTH_RESET' | 'AUTH_VERIFY' | 'AUTH_INVITE' | 'ONBOARDING' | 'PORTAL';

function DriverApp() {
  const [view, setView] = useState<ViewState>('LOADING');
  const [driverId, setDriverId] = useState('');
  const [firstName, setFirstName] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [inviteToken, setInviteToken] = useState('');
  const [employee, setEmployee] = useState<DriverEmployee | null>(null);
  const [portalTab, setPortalTab] = useState('home');
  const [showClockOut, setShowClockOut] = useState(false);
  const [showClockIn, setShowClockIn] = useState(false);

  useEffect(function() {
    // Detect URL params for entry flows
    const params = new URLSearchParams(window.location.search);

    // Invite token — manager-sent registration links land here with ?invite=TOKEN
    const urlInviteToken = params.get('invite');
    if (urlInviteToken) {
      setInviteToken(urlInviteToken);
      setView('AUTH_INVITE');
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }

    // Password reset token — email links land here with ?token=TOKEN
    const urlResetToken = params.get('token');
    if (urlResetToken) {
      setResetToken(urlResetToken);
      setView('AUTH_RESET');
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }

    // Verify flow — manager-sent links land here with ?verify=1
    if (params.get('verify')) {
      setView('AUTH_VERIFY');
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }

    const token = getToken();
    if (token) {
      api<HomeResponse>('/api/driver/home').then(function(data) {
        if (data.error) { clearToken(); setView('AUTH_LOGIN'); return; }
        setEmployee(data.employee || null);
        const flags = data.feature_flags || {};
        if (flags.first_login_gate && flags.first_login_gate.enabled && !data.employee?.profile_completed) {
          setView('ONBOARDING');
        } else {
          setView('PORTAL');
        }
      }).catch(function() { clearToken(); setView('AUTH_LOGIN'); });
    } else {
      setView('AUTH_LOGIN');
    }
  }, []);

  function handleLogout() {
    apiPost('/api/auth/logout', {}).then(function() {
      clearToken();
      setView('AUTH_LOGIN');
      setEmployee(null);
    });
  }

  function handleValidated(target: string, id: string, name: string) {
    setDriverId(id);
    setFirstName(name);
    if (target === 'SETUP') { setView('AUTH_SETUP'); }
    else if (target === 'HOME') {
      api<HomeResponse>('/api/driver/home').then(function(data) {
        if (data.employee) {
          setEmployee(data.employee);
          const flags = data.feature_flags || {};
          if (flags.first_login_gate && flags.first_login_gate.enabled && !data.employee.profile_completed) {
            setView('ONBOARDING');
          } else {
            setView('PORTAL');
          }
        }
      });
    }
  }

  if (view === 'LOADING') {
    return React.createElement('div', { style: { minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.dark } },
      React.createElement('div', { style: { fontFamily: FONT.mono, fontSize: 14, color: C.silver } }, 'Loading...')
    );
  }

  if (view === 'AUTH_LOGIN') {
    return React.createElement(LoginScreen, {
      onValidated: handleValidated,
      onForgot: function(id: string) { setDriverId(id); setView('AUTH_FORGOT'); }
    });
  }

  if (view === 'AUTH_SETUP') {
    return React.createElement(SetupScreen, {
      driverId, firstName,
      onComplete: function() { setView('AUTH_LOGIN'); },
      onBack: function() { setView('AUTH_LOGIN'); }
    });
  }

  if (view === 'AUTH_FORGOT') {
    return React.createElement(ForgotScreen, {
      driverId,
      onBack: function() { setView('AUTH_LOGIN'); }
    });
  }

  if (view === 'AUTH_RESET') {
    return React.createElement(ResetPasswordScreen, {
      token: resetToken,
      onComplete: function() { setView('AUTH_LOGIN'); },
      onBack: function() { setView('AUTH_LOGIN'); }
    });
  }

  if (view === 'AUTH_VERIFY') {
    return React.createElement(VerifyWizard, {
      onComplete: function() { setView('AUTH_LOGIN'); },
      onBack: function() { setView('AUTH_LOGIN'); }
    });
  }

  if (view === 'AUTH_INVITE') {
    return React.createElement(InviteFlow, {
      token: inviteToken,
      onBackToLogin: function() { setView('AUTH_LOGIN'); }
    });
  }

  if (view === 'ONBOARDING' && employee) {
    return React.createElement(DriverOnboardingWizard, {
      employee,
      onComplete: function() { setView('PORTAL'); }
    });
  }

  if (view === 'PORTAL') {
    if (showClockIn) {
      return React.createElement(ClockInFlow, { onDone: function() { setShowClockIn(false); setPortalTab('home'); } });
    }
    if (showClockOut) {
      return React.createElement(ClockOutFlow, { onDone: function() { setShowClockOut(false); setPortalTab('home'); } });
    }

    let screen: React.ReactNode;
    switch (portalTab) {
      case 'home': screen = React.createElement(HomeScreen, { employee, onLogout: handleLogout, onClockOut: function() { setShowClockOut(true); }, onClockIn: function() { setShowClockIn(true); } }); break;
      case 'stops': screen = React.createElement(StopsScreen); break;
      case 'schedule': screen = React.createElement(ScheduleScreen); break;
      case 'profile': screen = employee ? React.createElement(ProfileScreen, { employee, onLogout: handleLogout, onEmployeeUpdate: function(e: DriverEmployee) { setEmployee(e); } }) : null; break;
      default: screen = null;
    }

    return React.createElement('div', { style: { background: C.dark, minHeight: '100dvh' } },
      screen,
      React.createElement(BottomNav, { activeTab: portalTab, onTabChange: function(tab: string) { setPortalTab(tab); } })
    );
  }

  return null;
}

export default DriverApp;
